import {FC, useState} from 'react'
import {Component} from '../../types/ContentStructure'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

interface ComponentFAQProps {
    component: Component
    isEditable?: boolean
    setComponent?: any
    themes?: any
}

/**
 * FAQ
 *
 * Un componente FAQ = una pregunta+respuesta.
 * La sección padre (SECCION 227) itera sobre todos los hijos FAQ (258..262).
 * El primer FAQ (idx=0 en la sección) aparece abierto por defecto según mockup.
 *
 * El componentId viene del prop — lo usamos como panel id para el Accordion controlado
 * a nivel de la sección padre. Aquí manejamos estado local para mantener autonomía.
 */
const ComponentFAQ: FC<ComponentFAQProps> = ({component, isEditable = false, setComponent, themes}) => {
    const {title, text, theme} = component
    const [expanded, setExpanded] = useState(false)
    const textColot = theme.themeStyles.backgroundColor

    // Modo edición
    if (isEditable && setComponent) {
        return (
            <Box>
                {/* Aquí iría EditFields si existiera en WEB */}
                <Typography variant="body2" color="error">
                    Modo edición no implementado en WEB
                </Typography>
            </Box>
        )
    }

    return (
        <Box sx={{
            width: '100%',
            px: {xs: 2, sm: 2, md: 8},
        }}>
            <Accordion
                expanded={expanded}
                onChange={(_, isExpanded) => setExpanded(isExpanded)}
                disableGutters
                elevation={0}
                sx={{
                    backgroundColor: 'transparent',
                    '&:before': {display: 'none'},  // quitar línea MUI por defecto
                    textAlign: 'justify',
                }}
            >
                <AccordionSummary
                    expandIcon={
                        expanded
                            ? <RemoveIcon sx={{fontSize: 20, color: 'text.secondary'}}/>
                            : <AddIcon sx={{fontSize: 20, color: 'text.secondary'}}/>
                    }
                    sx={{
                        px: 0,
                        py: 1.5,
                        minHeight: 'unset',
                        '& .MuiAccordionSummary-content': {my: 0},
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: expanded ? 600 : 400,
                            fontSize: {xs: '0.875rem', md: '1rem'},
                            color: expanded ? textColot : 'text.primary',
                            transition: 'color 0.2s, font-weight 0.2s',
                            pr: 2,
                        }}
                    >
                        {title}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{px: 0, pt: 0, pb: 2}}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            lineHeight: 1.7,
                            fontSize: {xs: '0.8rem', md: '0.9rem'},
                            textWrap: 'pretty',
                        }}
                    >
                        {text && text.split('. ').map((paragraph, index) => (
                            <span key={index}>
                                {paragraph.trim()}
                                {index < text.split('. ').length - 1 && (<>.<br/></>)}
                            </span>
                        ))}
                    </Typography>
                </AccordionDetails>
            </Accordion>
            <Divider/>
        </Box>
    )
}

export default ComponentFAQ
